// Календарь выбора дат: один день или диапазон («туда — обратно», «заезд — выезд»).
// Поле — скрытый <input type="hidden" id=…> (значение YYYY-MM-DD) и кнопка .dp-display; окно .dp общее
// (открыто не больше одного), на телефоне — лист снизу с одним месяцем. Если передан источник данных,
// под каждой датой видны цена и занятость.
// Совместимость: запись input.value = '2026-11-15' извне (тесты, внешние скрипты) уходит в onChange.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useBackClose } from '../lib/backClose';
import { isoDate, plural, today } from '../lib/format';
import { Icon } from './Icon';

export type DatePhase = 'start' | 'end';

export interface DayInfo {
  text?: string;
  /** уровень цены 0–4 (lv-N) */
  level?: number | null;
  /** состояние дня (st-ok, st-soldout) */
  state?: string;
  title?: string;
}

export interface DateFieldSpec {
  id: string;
  label: string;
  value: string;
}

export interface DatePickerFieldsProps {
  start: DateFieldSpec;
  end?: DateFieldSpec;
  onChange: (start: string, end: string) => void;
  /** обратная дата не обязательна (кнопка «В одну сторону») */
  endOptional?: boolean;
  /** минимум ночей между датами (для отелей 1) */
  minNights?: number;
  /** подписи вкладок в окне календаря (по умолчанию — подписи полей) */
  labels?: { start?: string; end?: string };
  /** самая ранняя дата, по умолчанию сегодня */
  min?: string;
  data?: (month: string, phase: DatePhase) => Promise<Record<string, DayInfo> | null>;
  /** при смене ключа данные запрашиваются заново (города, валюта…) */
  dataKey?: string;
  legend?: (phase: DatePhase) => ReactNode;
  hiddenStart?: boolean;
  hiddenEnd?: boolean;
  /** дополнительное содержимое внутри .control поля (например, выбор месяца) */
  startExtra?: ReactNode;
  endExtra?: ReactNode;
  invalidStart?: boolean;
  invalidEnd?: boolean;
}

const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const DOW = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const valueDesc = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')!;
const getVal = (el: HTMLInputElement | null) => (el ? (valueDesc.get!.call(el) as string) : '');
const setVal = (el: HTMLInputElement | null, v: string) => { if (el) valueDesc.set!.call(el, v); };

const addMonths = (m: string, n: number) => { const d = new Date(`${m}-15T12:00:00`); d.setMonth(d.getMonth() + n); return isoDate(d).slice(0, 7); };
const addDays = (day: string, n: number) => { const d = new Date(`${day}T12:00:00`); d.setDate(d.getDate() + n); return isoDate(d); };
const fmt = (day: string) => new Date(`${day}T12:00:00`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', weekday: 'short' });
const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 760;

function nightsText(a: string, b: string) {
  const n = Math.round((Date.parse(b) - Date.parse(a)) / 86400000);
  return `${n} ${plural(n, 'ночь', 'ночи', 'ночей')}`;
}

// Открыт может быть только один календарь
let closeCurrent: (() => void) | null = null;

export function DatePickerFields(props: DatePickerFieldsProps) {
  const { start, end, endOptional, hiddenStart, hiddenEnd, startExtra, endExtra, invalidStart, invalidEnd } = props;
  const latest = useRef(props);
  latest.current = props;
  const startInput = useRef<HTMLInputElement>(null);
  const endInput = useRef<HTMLInputElement>(null);
  const startBtn = useRef<HTMLButtonElement>(null);
  const endBtn = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const cache = useRef(new Map<string, Record<string, DayInfo>>());
  const inflight = useRef(new Set<string>());
  const [, force] = useState(0);
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<DatePhase>('start');
  const [month, setMonth] = useState('');
  const [hover, setHover] = useState<string | null>(null);
  const [mobile, setMobile] = useState(isMobile);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const minDate = () => latest.current.min || today();

  // Значения владельца → скрытые поля
  useLayoutEffect(() => { if (getVal(startInput.current) !== start.value) setVal(startInput.current, start.value); }, [start.value]);
  useLayoutEffect(() => { if (end && getVal(endInput.current) !== end.value) setVal(endInput.current, end.value); }, [end?.value]);

  // Перехват .value: запись извне → onChange (истина — в самих полях, так две записи подряд не теряются)
  useLayoutEffect(() => {
    const pairs: [HTMLInputElement | null, DatePhase][] = [[startInput.current, 'start'], [endInput.current, 'end']];
    for (const [el, role] of pairs) {
      if (!el) continue;
      Object.defineProperty(el, 'value', {
        configurable: true,
        get() { return valueDesc.get!.call(this); },
        set(v: string) {
          const before = valueDesc.get!.call(this) as string;
          valueDesc.set!.call(this, v);
          if (before === v) return;
          const p = latest.current;
          const s = role === 'start' ? v : getVal(startInput.current);
          const e = role === 'end' ? v : getVal(endInput.current);
          if (s !== p.start.value || e !== (p.end ? p.end.value : '')) p.onChange(s, p.end ? e : '');
        },
      });
    }
    return () => {
      for (const [el] of pairs) if (el) delete (el as { value?: string }).value;
    };
  }, [Boolean(end)]);

  const close = useCallback(() => {
    setOpen(false);
    setHover(null);
    if (closeCurrent === close) closeCurrent = null;
  }, []);

  const openAt = (role: DatePhase) => {
    if (closeCurrent && closeCurrent !== close) closeCurrent();
    closeCurrent = close;
    const s = getVal(startInput.current);
    if (role === 'end' && !s) role = 'start';
    const v = role === 'start' ? s : getVal(endInput.current);
    setPhase(role);
    setMonth((v || s || minDate()).slice(0, 7));
    setMobile(isMobile());
    setPos(null);
    setOpen(true);
  };

  // Закрытие: щелчок снаружи, Esc; пересчёт положения при изменении окна
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t || popup.current?.contains(t) || t.closest('.dp-display')) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    const onResize = () => { setMobile(isMobile()); setPos(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  useEffect(() => () => { if (closeCurrent === close) closeCurrent = null; }, [close]);

  // Телефон: календарь — шторка во весь экран, «Назад» закрывает её
  useBackClose(open && mobile, close);

  // Положение окна под полем (или над ним, если снизу мало места): считаем один раз после открытия,
  // выбора первой даты или изменения окна (pos = null), — без повторных setState
  useLayoutEffect(() => {
    if (!open || mobile || pos || !popup.current) return;
    const btn = (phase === 'end' ? endBtn.current : startBtn.current) || startBtn.current;
    const anchor = btn?.closest('.field') || btn;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const w = popup.current.offsetWidth, h = popup.current.offsetHeight;
    const left = Math.max(12, Math.min(window.innerWidth - w - 12, r.left));
    let top = r.bottom + 8;
    if (top + h > window.innerHeight - 8) top = Math.max(8, r.top - h - 8);
    setPos({ left, top });
  }, [open, mobile, pos, phase, month]);

  // Цены и занятость подгружаем после показа — календарь не ждёт сеть
  const twoMonths = !mobile;
  const months = month ? (twoMonths ? [month, addMonths(month, 1)] : [month]) : [];
  const key = props.dataKey ?? '';
  const ck = (m: string) => `${key}|${phase}|${m}`;
  const missing = open && props.data ? months.filter((m) => !cache.current.has(ck(m)) && !inflight.current.has(ck(m))) : [];
  useEffect(() => {
    const load = latest.current.data;
    if (!load) return;
    for (const m of missing) {
      const cacheKey = ck(m);
      inflight.current.add(cacheKey); // не запрашиваем дважды
      load(m, phase).then((d) => {
        cache.current.set(cacheKey, d || {});
      }).catch(() => {}).finally(() => {
        inflight.current.delete(cacheKey);
        force((n) => n + 1);
      });
    }
  }, [missing.join(','), key, phase]);
  const loadingMonths = new Set(open && props.data ? months.filter((m) => !cache.current.has(ck(m))) : []);

  const setValues = (s: string, e: string) => {
    setVal(startInput.current, s);
    setVal(endInput.current, e);
    const p = latest.current;
    p.onChange(s, p.end ? e : '');
  };

  const pick = (day: string) => {
    const p = latest.current;
    let s = getVal(startInput.current), e = getVal(endInput.current);
    if (phase === 'start') {
      s = day;
      if (p.end && e && e < addDays(day, p.minNights || 0)) e = '';
      setValues(s, e);
      if (p.end) { setPhase('end'); setPos(null); } else close();
      return;
    }
    setValues(s, day);
    close();
  };

  const labels = { start: props.labels?.start ?? start.label, end: props.labels?.end ?? end?.label ?? 'Обратно' };
  const sVal = start.value, eVal = end ? end.value : '';

  const display = (role: DatePhase, v: string) => {
    if (!v) return <span className="dp-ph">{role === 'end' && endOptional ? 'Не нужно' : 'Выберите дату'}</span>;
    const d = new Date(`${v}T12:00:00`);
    return (
      <>
        <b>{`${d.getDate()} ${d.toLocaleDateString('ru-RU', { month: 'short' }).replace('.', '')}`}</b>
        <span>{d.toLocaleDateString('ru-RU', { weekday: 'short' })}</span>
      </>
    );
  };

  let popupNode: ReactNode = null;
  if (open && month) {
    const min = phase === 'end' && sVal ? addDays(sVal, props.minNights || 0) : minDate();
    const choosing = phase === 'end' ? labels.end : labels.start;
    const lg = props.legend ? props.legend(phase) : null;
    const todayIso = today();
    popupNode = createPortal(
      <div
        ref={popup}
        className={mobile ? 'dp dp-sheet' : 'dp'}
        role="dialog"
        style={mobile || !pos ? undefined : { left: `${pos.left}px`, top: `${pos.top}px` }}
      >
        <div className="dp-head">
          <div className="dp-tabs">
            <button type="button" className={`dp-tab ${phase === 'start' ? 'on' : ''}`} data-phase="start" onClick={() => setPhase('start')}>
              <small>{labels.start}</small><b>{sVal ? fmt(sVal) : '—'}</b>
            </button>
            {end && (
              <button type="button" className={`dp-tab ${phase === 'end' ? 'on' : ''}`} data-phase="end" onClick={() => { if (getVal(startInput.current)) setPhase('end'); }}>
                <small>{labels.end}</small><b>{eVal ? fmt(eVal) : '—'}</b>
              </button>
            )}
            {sVal && eVal && <span className="dp-nights">{nightsText(sVal, eVal)}</span>}
          </div>
          <button type="button" className="dp-x" aria-label="Закрыть" onClick={close}><Icon name="x" /></button>
        </div>
        <div className="dp-hint">
          Выберите дату: <b>{choosing.toLowerCase()}</b>
          {lg == null ? null : typeof lg === 'string' ? ` · ${lg}` : <> · {lg}</>}
        </div>
        <div className="dp-body" onMouseLeave={() => setHover(null)}>
          <button type="button" className="dp-nav prev" disabled={month <= minDate().slice(0, 7)} aria-label="Раньше" onClick={() => setMonth(addMonths(month, -1))}><Icon name="left" /></button>
          {months.map((m) => (
            <MonthGrid
              key={m}
              month={m}
              min={min}
              start={sVal}
              end={eVal}
              today={todayIso}
              data={cache.current.get(`${key}|${phase}|${m}`) || {}}
              loading={loadingMonths.has(m)}
              preview={phase === 'end' && sVal && hover ? { from: sVal, to: hover } : null}
              onPick={pick}
              onHover={setHover}
            />
          ))}
          <button type="button" className="dp-nav next" aria-label="Позже" onClick={() => setMonth(addMonths(month, 1))}><Icon name="right" /></button>
        </div>
        <div className="dp-foot">
          {end && endOptional
            ? <button type="button" className="ghost small-btn" data-oneway onClick={() => { setValues(getVal(startInput.current), ''); close(); }}><Icon name="arrow" />В одну сторону</button>
            : <span />}
          <button type="button" className="btn small-btn" data-done onClick={close}>Готово</button>
        </div>
      </div>,
      // Внутри модального окна (<dialog>) всё вне окна недоступно для нажатий — календарь рисуем в самом окне
      (startBtn.current?.closest('dialog') as HTMLElement | null) || document.body,
    );
  }

  return (
    <>
      <div className="field date">
        <label htmlFor={start.id}>{start.label}</label>
        <div className="control">
          <Icon name="calendar" />
          <input type="hidden" id={start.id} ref={startInput} className={invalidStart ? 'invalid' : undefined} />
          <button type="button" ref={startBtn} className={invalidStart ? 'dp-display invalid' : 'dp-display'} hidden={hiddenStart} onClick={() => openAt('start')}>
            {display('start', sVal)}
          </button>
          {startExtra}
        </div>
      </div>
      {end && (
        <div className="field date">
          <label htmlFor={end.id}>{end.label}</label>
          <div className="control">
            <Icon name="calendar" />
            <input type="hidden" id={end.id} ref={endInput} className={invalidEnd ? 'invalid' : undefined} />
            <button type="button" ref={endBtn} className={invalidEnd ? 'dp-display invalid' : 'dp-display'} hidden={hiddenEnd} onClick={() => openAt('end')}>
              {display('end', eVal)}
            </button>
            {endExtra}
          </div>
        </div>
      )}
      {popupNode}
    </>
  );
}

interface MonthGridProps {
  month: string;
  min: string;
  start: string;
  end: string;
  today: string;
  data: Record<string, DayInfo>;
  loading: boolean;
  preview: { from: string; to: string } | null;
  onPick: (day: string) => void;
  onHover: (day: string) => void;
}

function MonthGrid({ month, min, start, end, today: todayIso, data, loading, preview, onPick, onHover }: MonthGridProps) {
  const first = new Date(`${month}-01T12:00:00`);
  const count = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const offset = (first.getDay() + 6) % 7;
  const cells: ReactNode[] = DOW.map((d, i) => <span key={`w${i}`} className={i > 4 ? 'dp-dow we' : 'dp-dow'}>{d}</span>);
  for (let i = 0; i < offset; i++) cells.push(<span key={`o${i}`} />);
  for (let n = 1; n <= count; n++) {
    const day = `${month}-${String(n).padStart(2, '0')}`;
    const off = day < min;
    const info = off ? null : data[day]; // у недоступных дней цену не показываем
    const wd = (offset + n - 1) % 7;
    const cls = ['dp-day',
      off ? 'off' : '', wd > 4 ? 'we' : '', day === todayIso ? 'today' : '',
      day === start ? 'sel-start' : '', day === end ? 'sel-end' : '', day === start && !end ? 'solo' : '',
      start && end && day > start && day < end ? 'in-range' : '',
      info && info.state ? `st-${info.state}` : '',
      info && info.level != null ? `lv-${info.level}` : '',
      preview && day > preview.from && day <= preview.to ? 'pre' : '',
    ].filter(Boolean).join(' ');
    cells.push(
      <button
        key={day}
        type="button"
        className={cls}
        disabled={off}
        data-day={off ? undefined : day}
        title={info && info.title ? info.title : ''}
        onClick={off ? undefined : () => onPick(day)}
        onMouseEnter={off ? undefined : () => onHover(day)}
      >
        <span className="n">{n}</span>
        {info && info.text ? <span className="v">{info.text}</span> : loading && !off ? <span className="v dp-dots" /> : null}
      </button>,
    );
  }
  return (
    <div className="dp-month">
      <div className="dp-mtitle">{`${MONTHS[first.getMonth()]} ${first.getFullYear()}`}</div>
      <div className="dp-grid">{cells}</div>
    </div>
  );
}
